// Simplified data store functions for frontend2
// Based on frontend/src/stores/data.store.tsx

import ApiService from './api.service';

export type ChatThread = {
  thread_id: string;
  name: string;
  agent_type: string;
  created_by: string | null;  // null for agent-initiated conversations
  created_at: string;
  updated_at: string;
  shared_with: string[];
  is_owner: boolean;
};

export type ChatThreadMessage = {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool_call_id?: string;
  name?: string;
  tool_calls?: Array<{
    id: string;
    name: string;
    args: any;
  }>;
}

export type ChatThreadMessagesResult = {
  messages: ChatThreadMessage[];
  initial_message?: { role: string; content: string } | null;
};

export const getChatThreadMessages = async (threadId: string): Promise<ChatThreadMessagesResult> => {
  try {
    const result = (await ApiService.getInstance().request('GET', `/chat/threads/${threadId}/messages`)) as {
      thread_id: string;
      messages: ChatThreadMessage[];
      initial_message?: { role: string; content: string } | null;
    };
    return {
      messages: result.messages,
      initial_message: result.initial_message,
    };
  } catch (error: any) {
    // 404 is expected for new threads that don't exist yet
    // The thread will be created automatically when the first message is sent
    if (error?.code === 404) {
      return {
        messages: [],
      };
    }
    // Re-throw other errors
    throw error;
  }
};

export const listChatThreads = async (agentType?: string, limit: number = 50, userId?: string): Promise<ChatThread[]> => {
  const params: Record<string, any> = { limit };
  if (agentType) {
    params.agent_type = agentType;
  }
  // Pass user_id parameter - defaults to current user if not provided
  // If userId is explicitly provided, use it; otherwise backend will default to current user
  if (userId !== undefined) {
    params.user_id = userId;
  }
  const result = (await ApiService.getInstance().request('GET', '/chat/threads', params)) as {
    threads: ChatThread[];
  };
  return result.threads;
};

// Simple cache invalidation - in a real app you'd use React Query
export const invalidateChatThreads = () => {
  // This is a no-op in the simplified version
  // In the full version, this would invalidate React Query cache
};

